import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { fetchAboutPage } from "./supabase";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";
import type { I18nMap } from "./admin/TranslationsPanel";

type AboutPageRow = {
  id: number;
  title: string;
  subtitle?: string | null;
  body: string;
  image_url?: string | null;
  owners_title?: string | null;
  owners_body?: string | null;
  i18n?: I18nMap | null;
};

function localized(row: AboutPageRow, field: keyof AboutPageRow, lang: string): string {
  if (lang === "en" || !row.i18n) return (row[field] as string) ?? "";
  const translated = row.i18n[lang as keyof I18nMap] as Record<string, string> | undefined;
  return translated?.[field] ?? (row[field] as string) ?? "";
}

export function AboutPage() {
  const theme = useTheme();
  const { lang } = useLanguage();
  const [row, setRow] = useState<AboutPageRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutPage().then(({ data, error }) => {
      setLoading(false);
      if (!error && data) setRow(data as AboutPageRow);
    });
  }, []);

  if (loading) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto text-center">
        <p style={{ fontFamily: theme.fontBody, fontSize: "15px", color: theme.muted }}>Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-2xl mx-auto text-center">
        <p className="tracking-[0.2em] uppercase mb-4" style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}>
          Not found
        </p>
        <h1 className="mb-6" style={{ fontFamily: theme.fontHeading, fontSize: "28px", fontWeight: 400, color: theme.fg }}>
          About content is not available yet.
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 tracking-[0.2em] uppercase border transition-colors"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.primary, borderColor: theme.primary, borderRadius: theme.btnRadius }}
        >
          <ArrowLeft size={14} /> Home
        </Link>
      </div>
    );
  }

  const title = localized(row, "title", lang);
  const subtitle = localized(row, "subtitle", lang);
  const body = localized(row, "body", lang);
  const ownersTitle = localized(row, "owners_title", lang);
  const ownersBody = localized(row, "owners_body", lang);

  return (
    <div className="pt-28 pb-24 px-6">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-12 tracking-[0.15em] uppercase transition-opacity hover:opacity-80"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
        >
          <ArrowLeft size={14} /> Home
        </Link>

        <section className="mb-20">
          <p
            className="tracking-[0.3em] uppercase mb-3"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            About
          </p>
          <h1
            className="tracking-[0.08em] mb-4"
            style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className="leading-[1.8] mb-10 text-lg"
              style={{ fontFamily: theme.fontBody, fontSize: "18px", fontWeight: 300, color: theme.muted }}
            >
              {subtitle}
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {row.image_url && (
              <div className="overflow-hidden rounded-xl order-2 lg:order-1" style={{ backgroundColor: `${theme.border}40` }}>
                <ImageWithFallback
                  src={row.image_url}
                  alt={title}
                  className="w-full aspect-[4/5] object-cover"
                  width={600}
                  height={750}
                />
              </div>
            )}
            <div className={row.image_url ? "order-1 lg:order-2" : ""}>
              <div
                className="leading-[1.9]"
                style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.fg }}
              >
                {body.split(/\n\n+/).map((para, i) => (
                  <p key={i} className="mb-6 last:mb-0">
                    {para.split(/(\*\*.+?\*\*)/g).map((part, j) =>
                      part.startsWith("**") && part.endsWith("**") ? (
                        <strong key={j} style={{ fontWeight: 500 }}>{part.slice(2, -2)}</strong>
                      ) : (
                        <span key={j}>{part.split("\n").map((line, k) => (
                          <span key={k}>{line}{k < part.split("\n").length - 1 ? <br /> : null}</span>
                        ))}</span>
                      )
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {(ownersTitle || ownersBody) && (
          <section className="pt-16 border-t" style={{ borderColor: theme.border }}>
            <h2
              className="tracking-[0.06em] mb-6"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 400, color: theme.fg }}
            >
              {ownersTitle}
            </h2>
            <div
              className="leading-[1.9] max-w-2xl"
              style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
            >
              {ownersBody.split(/\n\n+/).map((para, i) => (
                <p key={i} className="mb-6 last:mb-0">
                  {para.split(/(\*\*.+?\*\*)/g).map((part, j) =>
                    part.startsWith("**") && part.endsWith("**") ? (
                      <strong key={j} style={{ fontWeight: 500, color: theme.fg }}>{part.slice(2, -2)}</strong>
                    ) : (
                      <span key={j}>{part.split("\n").map((line, k) => (
                        <span key={k}>{line}{k < part.split("\n").length - 1 ? <br /> : null}</span>
                      ))}</span>
                    )
                  )}
                </p>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
